// Story 7.12 Iteration 2 (ADO-911), DR-1 — the Product Detail Page.
//
// The PO-approved design refresh (Peter Eastwood + Fiona 2026-07-22, PO-approved
// 2026-07-28) introduced a full PDP. Iteration 1 recorded that NO product-detail
// page existed (D-5) and hosted the recommendation rails on the grid card as an
// interim; this page is the permanent home, and building it is an explicit
// customer ask recorded as deviation D-13 (it is not in the ADO #911 text).
//
// Route: `/products/:sku` (OQ-14 RESOLVED). Deep-linkable — see
// `useProductDetailStore` for how the product is resolved without a
// single-product endpoint (Iteration 2 is deliberately web-only).
//
// Page order, per Figma 1169:7024 / 1193:7661:
//   SiteHeader → breadcrumb → section-info line → hero card → three accordions
//   → "Often purchased together" carousel.
//
// The header is the shared catalogue-linking header — the PDP is a screen that LINKS
// to the catalogue (its search hands the term to /products; Browse opens the
// department mega-menu in place and a picked node hands off to /products).
// Its header carries the search typeahead (Story 7.2) and the barcode-scan control
// (Story 7.18), the review follow-up on ADO-1372 (Peter Eastwood 2026-08-19). That
// wiring lives in `CatalogueLinkingHeader`, which composes `useCatalogueHeader()` with
// those controls and is shared with every other signed-in page outside /products.

import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useSessionStore } from '../../auth/useSessionStore';
import { useCatalogueEntryKey } from '../../ordering/catalogueEntry';
import { destinationFor } from '../../ordering/orderingChoices';
import { isAdminRole } from '../../auth/rbac';
import type { CategoryNode, ProductSummary } from '../../api/browse.client';
import { useAvailability } from '../../availability/useAvailability';
import { usePrices } from '../../pricing/usePriceStore';
import { useOnOrder } from '../../onOrder/useOnOrder';
import { useProductDetailStore } from '../../product-detail/useProductDetailStore';
import { Breadcrumb } from '../Browse/Breadcrumb';
import { CatalogueLinkingHeader } from '../Products/CatalogueLinkingHeader';
import { ProductHero } from './ProductHero';
import { ProductPanels } from './ProductPanels';
import { PdpRecommendations } from './PdpRecommendations';
import styles from './ProductDetail.module.css';

/** Router-state handover from a listing card (skips the deep-link resolution). */
export interface ProductDetailHandover {
  readonly product?: ProductSummary;
  /** `true` when `product` is a recommendation STUB rather than a read-model row
   *  (set by `ProductCard partialRow` on the carousel and upsell surfaces). The
   *  page still adopts it — it has a name, an image and a pack size — but the
   *  entry is marked re-resolvable so `load()` fills in the missing fields. */
  readonly partial?: boolean;
  /** The ordering entry the card's row was read under (Enabler E7-C, ADO-2274, D-1). A row is
   *  its range's row, so the page seeds it only while that entry is still the selected one. */
  readonly catalogueEntryKey?: string | null;
}

export function ProductDetail() {
  const { sku = '' } = useParams<{ sku: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const role = useSessionStore((s) => s.role);

  const seed = useProductDetailStore((s) => s.seed);
  const load = useProductDetailStore((s) => s.load);
  const loadDetail = useProductDetailStore((s) => s.loadDetail);
  const entry = useProductDetailStore((s) => s.bySku[sku]);
  // Story 7.15 (ADO-1372) AC2 — the product's real category trail.
  const breadcrumb = useProductDetailStore((s) => s.detailBySku[sku]?.detail?.breadcrumb);
  // Enabler E7-C (ADO-2274), D-1: the product is read in the selected entry's range. An entry
  // change resets the store; the effect below re-runs on it and reads the product again.
  const catalogueEntryKey = useCatalogueEntryKey();
  // A depot switch (a change of entry) on this page goes to the new entry's destination (A10.1:
  // the product listing, or the landing page for a landing account) instead of reading the
  // product again in a range that may not list it. The page is opened under its first entry;
  // a deep link can render before the session has one, so that arrival is not a switch.
  const [openedUnderEntryKey, setOpenedUnderEntryKey] = useState(catalogueEntryKey);
  if (openedUnderEntryKey === null && catalogueEntryKey !== null) {
    setOpenedUnderEntryKey(catalogueEntryKey);
  }
  const entrySwitched = openedUnderEntryKey !== null && catalogueEntryKey !== openedUnderEntryKey;
  useEffect(() => {
    if (!entrySwitched) return;
    // `replace`: the product page was the old entry's, so history does not keep it. This is the
    // only navigation: the header switcher leaves an entry change on this page to it.
    navigate(destinationFor(useSessionStore.getState().activeTradingAccount), { replace: true });
  }, [entrySwitched, navigate]);

  // A card that navigated here already had the read-model row — adopt it rather
  // than re-resolving. Otherwise (deep link, refresh, a shared URL) resolve it.
  const handover = location.state as ProductDetailHandover | null;
  const handedProduct = handover?.product;
  const handedPartial = handover?.partial === true;
  // A card's row is its range's row: a handover produced under another entry is not seeded, so
  // `load` reads the selected entry's range instead. That covers an entry change on this page
  // and a history step back or forward to a location whose state an earlier entry produced.
  const handedEntryKey = handover?.catalogueEntryKey;
  useEffect(() => {
    if (sku === '' || entrySwitched) return;
    if (
      handedProduct !== undefined &&
      handedProduct.sku === sku &&
      handedEntryKey === catalogueEntryKey
    ) {
      seed(handedProduct, { partial: handedPartial });
    }
    // ALWAYS offered to `load`, including after a seed — `load` decides. A full
    // read-model handover leaves it a no-op (the entry is `loaded` and complete);
    // a STUB handover from a carousel tile or an upsell card is `partial`, so this
    // is what resolves the real row. The partial row stays on screen while it does.
    void load(sku);
    // Story 7.15 (ADO-1372) AC1/AC2 — the PDP detail (Product Information values +
    // breadcrumb) is loaded UNCONDITIONALLY for the SKU, because no handover can
    // carry it: a listing card hands over a `ProductSummary`, which leaves `load`
    // above a no-op. Without this call the panel would be populated on a deep link
    // and empty on a card click — the commonest arrival path.
    //
    // Not a second request: `load` stamps the detail slice `loading` synchronously
    // before it awaits (both read the same endpoint), so exactly one of the two
    // fetches, whichever claims it first.
    void loadDetail(sku);
    // `handedProduct` is safe in the deps: react-router's location (and therefore
    // its `state`) is a stable reference for the life of a navigation, so this
    // runs once per navigation rather than on every render. All three calls are
    // idempotent anyway — `seed` overwrites with the same row, and `load` /
    // `loadDetail` no-op while a fetch is in flight or the SKU is already resolved.
  }, [
    sku,
    load,
    loadDetail,
    seed,
    handedProduct,
    handedPartial,
    handedEntryKey,
    catalogueEntryKey,
    entrySwitched,
  ]);

  // After a switch nothing of the product renders while the page leaves, so no section below
  // reads it in the new entry or at the new depot.
  const product = entrySwitched ? null : (entry?.product ?? null);

  // Story 7.3 — the hero's stock pill, the Stock Card's session-depot row and the
  // Add button's UNAVAILABLE gate all read the availability store; this is what
  // populates it for the page's own SKU (the rails add their own tile SKUs).
  useAvailability(product === null ? [] : [product.sku]);
  // Pricing migration 17 (ADO-2248): one price request for the same products (no-op unless
  // VITE_REAL_PRICES_ENABLED is on).
  usePrices(product === null ? [] : [product.sku]);
  // Story 8.12 (ADO-2021) — whether the Customer already has it on order (the hero's On Order).
  useOnOrder(product === null ? [] : [product.sku]);

  const goToCatalogue = (node: CategoryNode | null) => {
    navigate('/products', node === null ? undefined : { state: { node } });
  };

  return (
    <div className={styles.page}>
      <CatalogueLinkingHeader showManagerDashboard={isAdminRole(role)} />

      <main className={styles.main} aria-label="Product detail">
        {product === null ? (
          <ProductDetailPlaceholder phase={entry?.phase} sku={sku} />
        ) : (
          <ResolvedProductDetail
            product={product}
            breadcrumb={breadcrumb ?? []}
            onNavigateCrumb={goToCatalogue}
          />
        )}
      </main>
    </div>
  );
}

/** Loading / not-found / error states — never a blank page, never a bare crash. */
function ProductDetailPlaceholder({
  phase,
  sku,
}: {
  readonly phase: string | undefined;
  readonly sku: string;
}) {
  if (phase === 'notFound') {
    return (
      <div className={styles.placeholder}>
        <h1 className={styles.placeholderTitle}>Product not found</h1>
        <p role="status">We couldn&apos;t find a product with the code {sku}.</p>
      </div>
    );
  }
  if (phase === 'error') {
    return (
      <div className={styles.placeholder}>
        <h1 className={styles.placeholderTitle}>Product unavailable</h1>
        <p role="alert">This product could not be loaded. Please try again.</p>
      </div>
    );
  }
  return (
    <div className={styles.placeholder}>
      <p role="status">Loading product…</p>
    </div>
  );
}

function ResolvedProductDetail({
  product,
  breadcrumb,
  onNavigateCrumb,
}: {
  readonly product: ProductSummary;
  /** Story 7.15 (ADO-1372) AC2 — root-first Department › Category › Sub-Category,
   *  from the product's own projected `category_path`. Empty for an uncategorised
   *  product, or while the detail read is still in flight. */
  readonly breadcrumb: readonly CategoryNode[];
  readonly onNavigateCrumb: (node: CategoryNode | null) => void;
}) {
  return (
    <div className={styles.content}>
      {/* DR-1 — Shop › Department › Category › product name.
          Story 7.15 (ADO-1372) AC2 closed gap G6: the ancestor crumbs are now REAL
          taxonomy nodes passed as `trail`, so they NAVIGATE to their listing like
          the Category Listing's own breadcrumb — they used to be inert `staticTrail`
          fixture labels because the category path was not on the read model.
          An empty trail renders the bare "Shop › {product name}", which is the
          correct answer for an uncategorised product (AC3), never an error. */}
      <Breadcrumb trail={breadcrumb} onNavigate={onNavigateCrumb} currentLabel={product.name} />

      {/* The "Ambient — Dublin · Your section: Chilled" line DR-1 called for was
          REMOVED from the design on 2026-07-29: the refreshed VC switcher in the
          site header now carries that context ("VC Dublin · a/c 44815 · delivery
          31/07"). Gap G8 (session environment/section context, Epic 8) is
          unchanged — it just no longer surfaces here. */}

      <ProductHero product={product} />
      <ProductPanels sku={product.sku} productName={product.name} />
      <PdpRecommendations productSku={product.sku} />
    </div>
  );
}

export default ProductDetail;
